import {
  useCreateLearnerNoteMutation,
  useUpdateLearnerNoteMutation,
} from '@/app/redux/endpoints/learnerNotes';
import { useAppSelector } from '@/app/redux/hooks';
import { useLearnerNote } from '@/app/redux/hooks/learnerNotes';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import EditorHeader from '@/common/ui/EditorHeader';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { NotFoundError } from '@/common/utils/error';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { useAutosave } from '@/common/utils/useAutosave';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import LearnerNoteEditor from './LearnerNoteEditor';

export default function LearnerNoteView() {
  const { studentId, noteId } = useParams<{
    studentId: string;
    noteId: string;
  }>();
  const isNew = noteId === 'new';
  const navigate = useNavigate();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const userId = useAppSelector(selectUserId);
  const {
    learnerNote: existingNote,
    isLoading,
    isFetching,
  } = useLearnerNote(isNew ? undefined : noteId);
  const [updateLearnerNote] = useUpdateLearnerNoteMutation();
  const [createLearnerNote] = useCreateLearnerNoteMutation();

  const [name, setName] = useState('Untitled Note');
  const [content, setContent] = useState('');
  const [lastSaved, setLastSaved] = useState(existingNote?.updatedAt);
  const [isReady, setIsReady] = useState(isNew);

  const { isSaving } = useAutosave({
    saveFn: handleSave,
    trigger: [name, content],
    disabled: !isReady,
  });

  // Sync name/content when note loads (e.g. direct URL navigation)
  useEffect(() => {
    if (existingNote && !isReady) {
      setName(existingNote.name);
      setContent(existingNote.content);
      setLastSaved(existingNote.updatedAt);
      setIsReady(true);
    }
  }, [existingNote]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNameChange = useCallback((newName: string) => {
    setName(newName);
  }, []);

  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
  }, []);

  const backPath = studentId
    ? `/dashboard/learners/${studentId}/notes`
    : '/dashboard/learners';

  async function handleSave() {
    if (!userId || !currentOrganisationId || !studentId) return;
    try {
      if (isNew) {
        const note = await createLearnerNote({
          createdBy: userId,
          organisationId: currentOrganisationId,
          studentId,
          name,
          content,
        }).unwrap();
        trackMixpanelEvent('Learner Note Created', {
          learnerNoteId: note?.id,
          studentId,
        });
        if (note) {
          setLastSaved(Date.now());
          navigate(`/dashboard/learners/${studentId}/notes/${note.id}`, {
            replace: true,
          });
        }
      } else {
        await updateLearnerNote({
          orgId: currentOrganisationId,
          studentId,
          id: noteId!,
          updates: { content, name },
        }).unwrap();
        setLastSaved(Date.now());
      }
    } catch {
      toast.error(isNew ? 'Failed to create note' : 'Failed to save', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  if (!isNew && !isLoading && !isFetching && !existingNote) {
    throw new NotFoundError('Note not found');
  }

  if (!isNew && !existingNote) return null;

  return (
    <div className="flex flex-col h-full">
      <EditorHeader
        name={name}
        onNameChange={handleNameChange}
        isSaving={isSaving}
        lastSaved={lastSaved}
        backPath={backPath}
      />
      <div className="flex-1 overflow-hidden">
        <LearnerNoteEditor
          content={content}
          onContentChange={handleContentChange}
        />
      </div>
    </div>
  );
}
